import { Component, inject, input, signal } from '@angular/core';
import { MatRipple } from '@angular/material/core';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { environment } from '../../../environments/environment';
import { AuthActions, AuthState } from '../../auth';
import { ThreadsListingComponent } from '../../chats/components/threads-listing.component';
import { ChatActions, ChatsActions } from '../../chats/store/chats.actions';
import { ChatsState, ItemNavigation } from '../../chats/store/chats.reducer';
import {
  navigationButtonAnimation,
  sidebarAnimation,
  sidebarToggleIconAnimation,
  threadListingAnimation,
  userInfoAnimation,
} from '../../shared/animations/sidebar.animation';
import {
  AvatarDirective,
  ModalActionType,
  ModalComponent,
  UIBasicIconsEnum,
  UseArrowIconDirective,
  UseLogoDirective,
  UseUIBasicIconDirective,
} from '../../ui-components';
import { LayoutActions } from '../store/layout.actions';
import { layoutFeature, LayoutState } from '../store/layout.reducer';
@Component({
  selector: 'dmcom-sidebar-desktop',
  animations: [sidebarAnimation, navigationButtonAnimation, sidebarToggleIconAnimation, threadListingAnimation, userInfoAnimation],
  imports: [
    AvatarDirective,
    UseLogoDirective,
    UseUIBasicIconDirective,
    UseArrowIconDirective,
    MatMenuModule,
    TranslateModule,
    MatRipple,
    ThreadsListingComponent,
    ModalComponent,
  ],
  template: `
    <div
      id="sidebar-container"
      class="sidebar-container h-full pl-20 pr-20 pt-40  pb-20 flex flex-col items-start color-alert-banner-neutral"
      [@sidebarAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
    >
      <div class="flex items-center w-full pl-8 mb-24 ">
        <img class="w-32 h-32" dmcomUseLogo="logo-docmagic" />
        <div class="flex items-center w-full justify-end h-32">
          <svg
            dmcomUseUIBasicIcon="sliderOpened"
            class="sidebar-toggle-icon w-24 h-24 text-grayscale-gray-darker bg-transparent hover:bg-grayscale-gray-light-48 rounded-lg cursor-pointer"
            [@sidebarToggleIconAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
            [class.pointer-events-none]="!isSidebarOpened()"
            [class.pointer-events-auto]="isSidebarOpened()"
            (click)="toggleSidebar()"
          ></svg>
        </div>
      </div>

      <div class="flex items-center h-56 w-full" [class.pl-4]="!isSidebarOpened()">
        <button
          class="navigation-button flex flex-row items-center justify-start gap-12 w-full text-left bg-transparent border-none hover:bg-grayscale-gray-light-48 transition-colors duration-200 rounded-lg cursor-pointer"
          [class.pl-4]="!isSidebarOpened()"
          [class.pl-8]="isSidebarOpened()"
          (click)="onTriggerAction('new_request')()"
          [@navigationButtonAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
        >
          <svg dmcomUseUIBasicIcon="edit" class="min-w-24 min-h-24 w-24 h-24 text-primary-500"></svg>
          <div class="flex flex-col overflow-hidden transition-all duration-300 ease-in-out">
            <span class="font-bold text-grayscale-black text-[14px] whitespace-nowrap">New Request</span>
            <span class="text-grayscale-gray-darker text-[12px] whitespace-nowrap mt-2">Ask Compliance <em>Edge</em></span>
          </div>
        </button>
      </div>

      <div class="flex items-center h-56 mb-24 w-full" [class.pl-4]="!isSidebarOpened()">
        <button
          class="navigation-button flex flex-row  items-center justify-start gap-12 w-full text-left border-none transition-colors duration-200 rounded-lg cursor-pointer"
          [class.pl-4]="!isSidebarOpened()"
          [class.pl-8]="isSidebarOpened()"
          [class.bg-primary-50]="router.url === '/articles'"
          [class.bg-transparent]="router.url !== '/articles'"
          [class.hover:bg-grayscale-gray-light-48]="router.url !== '/articles'"
          (click)="onTriggerAction('discover')()"
          [@navigationButtonAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
        >
          <svg dmcomUseUIBasicIcon="discover" class="min-w-24 min-h-24 w-24 h-24 text-primary-500"></svg>
          <div class="flex flex-col overflow-hidden transition-all duration-300 ease-in-out">
            <span class="font-bold text-grayscale-black text-[14px] whitespace-nowrap">Discover</span>
            <span class="text-grayscale-gray-darker text-[12px] whitespace-nowrap mt-2">Latest compliance updates</span>
          </div>
        </button>
      </div>

      <div
        class="thread-listing flex-1 h-full overflow-y-auto w-full max-w-[256px]"
        [@threadListingAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
      >
        <dmcom-threads-listing-component
          class="max-w-[256px]"
          [itemNavigations]="itemNavigations()"
          (itemClicked)="handleItemClicked($event)"
        >
        </dmcom-threads-listing-component>
      </div>

      <div class="w-full mt-8 mb-8 flex flex-col items-center">
        <!-- User info section -->
        <div class="w-full flex flex-col items-center">
          <div
            class="flex items-center w-full cursor-pointer px-8 py-8 rounded-lg hover:bg-neutral-800"
            [class.justify-center]="!isSidebarOpened()"
            [matMenuTriggerFor]="userMenu"
            [matMenuTriggerData]="{ x: 'after', y: 'top' }"
          >
            <div
              dmcomAvatar
              size="md"
              class="pointer-events-none text-white bg-primary-500"
              style="width: 36px; height: 36px; min-width: 36px; min-height: 36px;"
            >
              {{ userInitials() }}
            </div>
            <div
              class="user-info-text flex flex-1 flex-col ml-8 overflow-hidden"
              [@userInfoAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
            >
              <span class="text-[14px] text-grayscale-black whitespace-nowrap">
                {{ userName() }}
              </span>
              <!-- <button
                class="bg-transparent hover:text-primary-400 text-primary-500 text-[12px] rounded-lg transition-colors duration-200 text-left whitespace-nowrap"
              >
                Upgrade
              </button> -->
            </div>
            <svg
              dmcomUseArrowIcon="chevron-down"
              class="user-chevron w-16 h-16 text-neutral-400"
              [@userInfoAnimation]="isSidebarOpened() ? 'opened' : 'closed'"
            ></svg>
          </div>
        </div>

        <mat-menu xPosition="after" yPosition="below" #userMenu="matMenu" class="user-dropdown-menu">
          <button mat-menu-item (click)="onTriggerAction('logout')()">
            {{ 'HOME.LOGOUT' | translate }}
          </button>
          <button mat-menu-item (click)="onTriggerAction('delete_chats')()">
            {{ 'HOME.DELETE_CHATS' | translate }}
          </button>
        </mat-menu>

        <!-- Content that should fade in/out -->
        <div class="bottom-section w-full flex flex-col items-center" [class.opacity-0]="!isSidebarOpened()">
          <!-- Separator line -->
          <div class="w-full h-px bg-neutral-600 mt-8 mb-16"></div>

          <!-- Terms and Privacy Policy buttons -->
          <div class="flex items-center w-full  pl-8 gap-16">
            <button
              matRipple
              class="text-[14px] text-neutral-400 hover:text-text-neutral-enabled cursor-pointer whitespace-nowrap"
              (click)="onTriggerAction('terms')()"
            >
              Terms
            </button>

            <button
              matRipple
              class="text-[14px] text-neutral-400 hover:text-text-neutral-enabled cursor-pointer whitespace-nowrap"
              (click)="onTriggerAction('privacy_policy')()"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Delete Chats Confirmation Modal -->
    <dmcom-modal
      [modalOptions]="deleteChatsModalOptions()"
      [isModalVisible]="isDeleteChatsModalVisible()"
      (triggerAction)="onModalAction($event)"
    >
    </dmcom-modal>
  `,
})
export class SidebarDesktopResponsive {
  private readonly authStore = inject<Store<AuthState>>(Store);
  public readonly router = inject<Router>(Router);
  private readonly chatsStore = inject<Store<ChatsState>>(Store);
  private readonly layoutStore = inject<Store<LayoutState>>(Store);
  private readonly translate = inject(TranslateService);

  public readonly userInitials = input.required<string>();
  public readonly userName = input.required<string>();
  public readonly isSidebarOpened = this.layoutStore.selectSignal(layoutFeature.selectIsSidebarOpened);
  public readonly itemNavigations = input.required<ItemNavigation[]>();

  // Modal state management
  public readonly isDeleteChatsModalVisible = signal(false);
  public readonly deleteChatsModalOptions = signal({
    title: this.translate.instant('HOME.DELETE_CHATS_CONFIRMATION.TITLE') as string,
    description: this.translate.instant('HOME.DELETE_CHATS_CONFIRMATION.DESCRIPTION') as string,

    icon: UIBasicIconsEnum.delete,
    showCloseButton: true,
    backdropDismiss: true,
    isFullscreen: false,
    actions: [
      {
        label: this.translate.instant('HOME.DELETE_CHATS_CONFIRMATION.CANCEL') as string,
        action: 'cancel',
        type: ModalActionType.cancel,
        disabled: false,
      },
      {
        label: this.translate.instant('HOME.DELETE_CHATS_CONFIRMATION.CONFIRM') as string,
        action: 'confirm',
        type: ModalActionType.delete,
        disabled: false,
      },
    ],
  });

  onTriggerAction(action: 'terms' | 'privacy_policy' | 'logout' | 'new_request' | 'discover' | 'delete_chats') {
    return () => {
      switch (action) {
        case 'terms':
          window.open(environment.appTermsUrl, '_blank');
          break;
        case 'privacy_policy':
          window.open(environment.appPrivacyPolicyUrl, '_blank');
          break;
        case 'logout':
          this.authStore.dispatch(AuthActions.logout());
          break;
        case 'new_request':
          this.chatsStore.dispatch(ChatActions.selectEmptyChat());
          void this.router.navigate(['/request']);
          break;
        case 'discover':
          this.chatsStore.dispatch(ChatActions.unselectChat());
          void this.router.navigate(['/articles']);
          break;
        case 'delete_chats':
          this.isDeleteChatsModalVisible.set(true);
          break;
      }
    };
  }

  toggleSidebar() {
    this.layoutStore.dispatch(
      LayoutActions.setSidebarOpened({
        isOpened: !this.isSidebarOpened(),
      }),
    );
  }

  handleItemClicked(item: ItemNavigation) {
    this.chatsStore.dispatch(ChatActions.selectChat({ id: item.id, navigateToRequest: true }));
  }

  onModalAction(action: string) {
    switch (action) {
      case 'cancel':
        this.isDeleteChatsModalVisible.set(false);
        break;
      case 'confirm':
        this.chatsStore.dispatch(ChatsActions.deleteChats());
        this.isDeleteChatsModalVisible.set(false);
        break;
    }
  }
}
